/**
 * Face card motifs: Ace, Jack, Queen, King.
 *
 * Each motif function takes the root SVG parent and a color, and appends motif
 * geometry centered at (125, 175) inside the card's 250x350 viewBox. `addFaceMotif`
 * is the dispatcher used by `createCard` for jack/queen/king; `addAceMotif` is called
 * directly for aces (hero-scale central suit pip with enhanced atmosphere).
 *
 * `getAtmosphere` is the concentric background-circle helper all motifs share. It
 * lives here because its only callers are the motif functions.
 *
 * `addAceMotif` uses a per-suit dispatcher — like `addArtisanPip` in `card-pips.js` —
 * so each suit's ace geometry lives in its own module-private drawer. `addKingMotif`
 * splits crown rendering into base/points/center layer helpers.
 */

import { s } from "./card-svg.js";
import { adjustGradient } from "./card-gradients.js";
import { CARD_GEOMETRY } from "./card-geometry.js";

/**
 * Wraps a motif build in a group scaled about the card center (125, 175) per the caller's
 * resolved `CARD_GEOMETRY` scale knob — `motifScaleJack`/`Queen`/`King` for face ranks, and
 * `motifScaleAceDiamonds`/`Hearts`/`Spades`/`Clubs` per suit for aces. Verdict + why for each
 * knob's value: CARDS.md § Golden-ratio verdicts.
 *
 * At a knob value of 1 no wrapper is created at all, so `addFaceMotif`/`addAceMotif` output stays
 * byte-identical to before this knob existed (existing snapshots assert exact `outerHTML`). The
 * wrapper is attached to `parent` BEFORE `build` runs (not after) — `adjustGradient` resolves
 * its shared defs via `parent.ownerSVGElement`, which only works once `parent` is actually part
 * of the card's SVG tree; a detached group would silently fail that lookup for a per-card
 * (trump-reveal-pending) gradient master.
 */
function withMotifScale(parent, scale, build) {
  if (scale === 1) {
    build(parent);
    return;
  }
  const wrap = s("g", {
    transform: `translate(125, 175) scale(${scale}) translate(-125, -175)`,
  });
  parent.appendChild(wrap);
  build(wrap);
}

/**
 * Concentric atmosphere circles for face card backgrounds. The ace side keeps two rings and a
 * scale knob (`atmosphereScaleAce`, default 1 reproduces today's literal radii exactly) — a
 * multiplier keeps both rings proportional to each other, the reason `withMotifScale` needs a
 * wrapper but a plain circle's `r` doesn't. The face side has only one ring, so there's nothing
 * for a scale to stay proportional TO — `faceAtmosphereRadius` (default 91) IS that ring's
 * radius directly, the same "knob holds the value itself" shape `mandalaHRadius` already uses.
 * Tagged `data-atmosphere` (present at every value, including the defaults) so the Golden tab's
 * glyph-only readouts (motif-height, touches-corner) can structurally exclude it without parsing
 * the rest of the motif's mixed geometry — the deliberate, documented exception to this knob
 * work otherwise leaving production markup untouched.
 *
 * Appended OUTSIDE the motif-scale wrapper by both motif entry points (decoupled 2026-07-17,
 * DESIGN #15): the ace's outer ring at rest (r=95) already sits at the corner-pip boundary
 * (~95.6 units from the card center), so ANY motif boldness that dragged the glow along pushed
 * it into the corner pips — Andre's rule is the glow must never touch them. The atmosphere knobs
 * alone govern glow size; the motif knobs scale the glyph only.
 */
/** One atmosphere ring, centered on the card. Shared by both branches below (getAtmosphere's own
 * size-budget headroom) rather than three near-duplicate literal circle() calls. */
function atmosphereRing(r, opacity, color) {
  return s("circle", { cx: 125, cy: 175, r, fill: color, opacity });
}

export function getAtmosphere(color, isAce = false) {
  const g = s("g", { "data-atmosphere": "" });
  if (isAce) {
    const { atmosphereScaleAce } = CARD_GEOMETRY;
    g.appendChild(atmosphereRing(80 * atmosphereScaleAce, 0.06, color));
    g.appendChild(atmosphereRing(95 * atmosphereScaleAce, 0.06, color));
  } else {
    g.appendChild(
      atmosphereRing(CARD_GEOMETRY.faceAtmosphereRadius, 0.08, color),
    );
  }
  return g;
}

// --- Per-suit ace drawers (appended to a pre-transformed <g>, gColor already resolved) ---

/** Schellen: orbing sphere with etched slit and faceted highlight. */
function addDiamondAce(g, gColor) {
  g.appendChild(
    s("circle", { cx: 0, cy: 0, r: 54, fill: gColor, opacity: 0.9 }),
  );
  g.appendChild(
    s("path", {
      d: "M -32 16 Q 0 40 32 16",
      fill: "none",
      stroke: "#E2E8F0",
      "stroke-width": 6,
      "stroke-linecap": "round",
      opacity: 0.5,
    }),
  );
  g.appendChild(
    s("circle", { cx: 18, cy: -18, r: 14, fill: "#E2E8F0", opacity: 0.25 }),
  );
  g.appendChild(
    s("rect", { x: -8, y: -76, width: 16, height: 14, rx: 4, fill: gColor }),
  );
}

/** Herz: heart silhouette with radiant core. */
function addHeartAce(g, gColor) {
  g.appendChild(
    s("path", {
      d: "M 0 76 C -16 54 -64 27 -64 -16 C -64 -49 -38 -65 -16 -65 C -5 -65 0 -49 0 -11 C 0 -49 5 -65 16 -65 C 38 -65 64 -49 64 -16 C 64 27 16 54 0 76 Z",
      fill: gColor,
      opacity: 0.9,
    }),
  );
  g.appendChild(
    s("circle", { cx: 0, cy: 11, r: 22, fill: "#E2E8F0", opacity: 0.12 }),
  );
  g.appendChild(
    s("circle", { cx: 0, cy: 11, r: 11, fill: "#E2E8F0", opacity: 0.2 }),
  );
}

/** Grün: glow-leaf silhouette with radiating midrib. */
function addSpadeAce(g, gColor) {
  const haloD =
    "M 0 -92 C 40 -65 75 -27 75 11 C 75 43 49 54 32 70 L 0 92 L -32 70 C -49 54 -75 43 -75 11 C -75 -27 -40 -65 0 -92 Z";
  const leafD =
    "M 0 -76 C 32 -54 60 -27 60 3 C 60 27 38 41 27 54 L 0 76 L -27 54 C -38 41 -60 27 -60 3 C -60 -27 -32 -54 0 -76 Z";
  g.appendChild(s("path", { d: haloD, fill: gColor, opacity: 0.15 }));
  g.appendChild(s("path", { d: leafD, fill: gColor, opacity: 0.9 }));
  appendSpadeAceVeins(g);
}

/** Vellum midrib stripe + radiating ribs over the leaf body. */
function appendSpadeAceVeins(g) {
  g.appendChild(
    s("line", {
      x1: 0,
      y1: -54,
      x2: 0,
      y2: 54,
      stroke: "#E2E8F0",
      "stroke-width": 5,
      opacity: 0.4,
    }),
  );
  g.appendChild(
    s("path", {
      d: "M 0 -27 L 32 -5 M 0 5 L 27 27 M 0 -27 L -32 -5 M 0 5 L -27 27",
      fill: "none",
      stroke: "#E2E8F0",
      "stroke-width": 2.5,
      opacity: 0.25,
    }),
  );
}

/** Eichel: acorn ellipse with diamond-quilted cap and radiant seed. */
function addClubAce(g, gColor) {
  g.appendChild(
    s("ellipse", { cx: 0, cy: 16, rx: 44, ry: 54, fill: gColor, opacity: 0.9 }),
  );
  g.appendChild(
    s("circle", { cx: 0, cy: 16, r: 19, fill: "#E2E8F0", opacity: 0.12 }),
  );
  g.appendChild(
    s("path", {
      d: "M -54 5 Q -54 -49 0 -49 Q 54 -49 54 5 L -54 5 Z",
      fill: gColor,
    }),
  );
  g.appendChild(
    s("path", {
      d: "M -11 -11 L 0 -19 L 11 -11 L 0 -3 Z",
      fill: "none",
      stroke: "#E2E8F0",
      "stroke-width": 1.6,
      opacity: 0.3,
    }),
  );
  g.appendChild(
    s("rect", { x: -3, y: -70, width: 6, height: 11, rx: 2, fill: gColor }),
  );
}

const ACE_MOTIF_SCALE_KEY = {
  diamonds: "motifScaleAceDiamonds",
  hearts: "motifScaleAceHearts",
  spades: "motifScaleAceSpades",
  clubs: "motifScaleAceClubs",
};

/** Ace (Daus): Hero-scale central suit pip with enhanced atmosphere. */
export function addAceMotif(parent, suit, color) {
  const scale = CARD_GEOMETRY[ACE_MOTIF_SCALE_KEY[suit]];
  parent.appendChild(getAtmosphere(color, true)); // outside the scale wrapper — see getAtmosphere
  withMotifScale(parent, scale, (target) => {
    const gColor = adjustGradient(target, color, 175, 1.1);
    const g = s("g", { transform: "translate(125, 175) scale(1.1)" });
    switch (
      suit // size: flat dispatch
    ) {
      case "diamonds":
        addDiamondAce(g, gColor);
        break;
      case "hearts":
        addHeartAce(g, gColor);
        break;
      case "spades":
        addSpadeAce(g, gColor);
        break;
      case "clubs":
        addClubAce(g, gColor);
        break;
    }
    target.appendChild(g);
  });
}

/** Jack (Unter): crossed broadswords motif. */
function addJackMotif(parent, color) {
  const cx = 125;
  const cy = 175;
  appendJackCrossedSwords(parent, cx, cy, color);
  appendJackCenterBoss(parent, cx, cy, color);
}

/** Builds and appends two swords rotated -45° / +45° around the card center. */
function appendJackCrossedSwords(parent, cx, cy, color) {
  const sword = buildJackSword();
  const g = s("g", {
    fill: color,
    opacity: 0.9,
    transform: `translate(${cx}, ${cy}) scale(1.1)`,
  });
  for (const angle of [-45, 45]) {
    const wrap = s("g", { transform: `rotate(${angle})` });
    wrap.appendChild(sword.cloneNode(true));
    g.appendChild(wrap);
  }
  parent.appendChild(g);
}

/** Returns a sword <g> (blade triangle, vellum highlight, hilt). Designed to be `cloneNode`d. */
function buildJackSword() {
  const sword = s("g");
  sword.appendChild(s("path", { d: "M -10 -40 L 10 -40 L 0 80 Z" }));
  sword.appendChild(
    s("line", {
      x1: 0,
      y1: -35,
      x2: 0,
      y2: 70,
      stroke: "#E2E8F0",
      "stroke-width": 1.2,
      opacity: 0.3,
    }),
  );
  appendJackHilt(sword);
  return sword;
}

/** Crossguard, grip, pommel, and pommel highlight — appended as a translated subgroup. */
function appendJackHilt(sword) {
  const hilt = s("g", { transform: "translate(0, -40)" });
  hilt.appendChild(
    s("rect", { x: -20, y: -8, width: 40, height: 10, rx: 2.5 }),
  );
  hilt.appendChild(
    s("rect", {
      x: -14,
      y: -5,
      width: 28,
      height: 2,
      rx: 1,
      fill: "#E2E8F0",
      opacity: 0.25,
    }),
  );
  hilt.appendChild(s("rect", { x: -6, y: -24, width: 12, height: 16, rx: 2 }));
  hilt.appendChild(s("circle", { cx: 0, cy: -30, r: 10 }));
  hilt.appendChild(
    s("circle", { cx: 0, cy: -30, r: 4, fill: "#E2E8F0", opacity: 0.2 }),
  );
  sword.appendChild(hilt);
}

/** Central boss where the swords meet: filled circle, vellum glow, embossed cross. */
function appendJackCenterBoss(parent, cx, cy, color) {
  parent.appendChild(s("circle", { cx, cy, r: 20, fill: color }));
  parent.appendChild(
    s("circle", { cx, cy, r: 10, fill: "#E2E8F0", opacity: 0.15 }),
  );
  parent.appendChild(
    s("path", {
      d: "M -5 -5 L 5 5 M -5 5 L 5 -5",
      transform: `translate(${cx}, ${cy}) scale(1.1)`,
      stroke: "#E2E8F0",
      "stroke-width": 2,
      opacity: 0.4,
      "stroke-linecap": "round",
    }),
  );
}

/** Queen (Ober): Reichsapfel (Imperial orb) motif. */
function addQueenMotif(parent, color) {
  const cx = 125;
  const cy = 189; // 175 + 14 from mockup
  const r = 60;
  parent.appendChild(s("circle", { cx, cy, r, fill: color, opacity: 0.9 }));
  appendQueenStars(parent, cx, cy);
  parent.appendChild(
    s("circle", { cx, cy, r: 24, fill: "#E2E8F0", opacity: 0.1 }),
  );
  parent.appendChild(
    s("circle", { cx, cy, r: 10, fill: "#E2E8F0", opacity: 0.2 }),
  );
  appendQueenBands(parent, cx, cy, r, color);
  appendQueenCross(parent, cx, cy, color);
}

/** Three vellum dots over the orb plus thin connectors between consecutive dots. */
function appendQueenStars(parent, cx, cy) {
  const stars = s("g", { opacity: 0.2 });
  const points = [
    { dx: -16, dy: -22, r: 1.6 },
    { dx: 20, dy: 2, r: 2.4 },
    { dx: 8, dy: 18, r: 1.2 },
  ];
  for (const { dx, dy, r } of points) {
    stars.appendChild(
      s("circle", { cx: cx + dx, cy: cy + dy, r, fill: "#E2E8F0" }),
    );
  }
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    stars.appendChild(
      s("line", {
        x1: cx + a.dx,
        y1: cy + a.dy,
        x2: cx + b.dx,
        y2: cy + b.dy,
        stroke: "#E2E8F0",
        "stroke-width": 0.4,
      }),
    );
  }
  parent.appendChild(stars);
}

/** Equator ellipse and meridian curve, each painted twice (color stroke + vellum highlight). */
function appendQueenBands(parent, cx, cy, r, color) {
  const equator = { cx, cy, rx: r, ry: 18, fill: "none" };
  parent.appendChild(
    s("ellipse", { ...equator, stroke: color, "stroke-width": 6 }),
  );
  parent.appendChild(
    s("ellipse", {
      ...equator,
      stroke: "#E2E8F0",
      "stroke-width": 1.2,
      opacity: 0.3,
    }),
  );
  const meridianD = `M ${cx} ${cy - 60} Q ${cx + 28} ${cy} ${cx} ${cy + 60}`;
  parent.appendChild(
    s("path", { d: meridianD, fill: "none", stroke: color, "stroke-width": 6 }),
  );
  parent.appendChild(
    s("path", {
      d: meridianD,
      fill: "none",
      stroke: "#E2E8F0",
      "stroke-width": 1.2,
      opacity: 0.2,
    }),
  );
}

/** Vertical bar + horizontal bar + vellum highlight forming the orb's surmounting cross. */
function appendQueenCross(parent, cx, cy, color) {
  const rects = [
    { x: cx - 10, y: cy - 106, width: 20, height: 52, rx: 4, fill: color },
    { x: cx - 32, y: cy - 90, width: 64, height: 16, rx: 4, fill: color },
    {
      x: cx - 2,
      y: cy - 98,
      width: 4,
      height: 20,
      rx: 2,
      fill: "#E2E8F0",
      opacity: 0.3,
    },
  ];
  for (const r of rects) parent.appendChild(s("rect", r));
}

/** King (König): ornate Germanic crown motif. */
function addKingMotif(parent, color) {
  const cx = 125,
    cy = 175;
  const gColor = adjustGradient(parent, color, cy, 0.8);
  const g = s("g", { transform: `translate(${cx}, ${cy}) scale(0.8)` });
  appendCrownBase(g, gColor);
  appendCrownPoints(g, gColor);
  appendCrownCenter(g, gColor);
  parent.appendChild(g);
}

/** Crown base: flanking accents, banded rim, and rim jewels. */
function appendCrownBase(g, gColor) {
  const bgAccents = s("g", { fill: gColor, opacity: 0.12 });
  bgAccents.appendChild(s("path", { d: "M -100 40 L -75 -40 L -50 40 Z" }));
  bgAccents.appendChild(s("path", { d: "M 100 40 L 75 -40 L 50 40 Z" }));
  g.appendChild(bgAccents);
  appendCrownRim(g, gColor);
  const jewels = s("g", { fill: "#E2E8F0", opacity: 0.4 });
  [-80, -30, 30, 80].forEach((x) =>
    jewels.appendChild(s("circle", { cx: x, cy: 52, r: 2.5 })),
  );
  g.appendChild(jewels);
}

/** Banded rim: filled rectangle + outline, both sharing the same x/y/width/height/rx. */
function appendCrownRim(g, gColor) {
  const rim = { x: -105, y: 40, width: 210, height: 24, rx: 2 };
  g.appendChild(s("rect", { ...rim, fill: gColor, opacity: 0.3 }));
  g.appendChild(
    s("rect", { ...rim, fill: "none", stroke: gColor, "stroke-width": 4 }),
  );
}

/** Crown peaks: four fleur-de-lis spires with internal highlights and inner-peak stars. */
function appendCrownPoints(g, gColor) {
  const points = s("g", { fill: gColor, opacity: 0.9 });
  points.appendChild(s("path", { d: "M -55 40 L -25 -100 L 5 40 Z" }));
  points.appendChild(s("path", { d: "M 55 40 L 25 -100 L -5 40 Z" }));
  points.appendChild(s("path", { d: "M -105 40 L -75 -60 L -45 40 Z" }));
  points.appendChild(s("path", { d: "M 105 40 L 75 -60 L 45 40 Z" }));
  g.appendChild(points);

  const highlights = s("g", { fill: "#E2E8F0", opacity: 0.15 });
  highlights.appendChild(s("path", { d: "M -25 -100 L -35 -20 L -15 -20 Z" }));
  highlights.appendChild(s("path", { d: "M 25 -100 L 15 -20 L 35 -20 Z" }));
  g.appendChild(highlights);

  const stars = s("g", { fill: "#E2E8F0", opacity: 0.8 });
  const starPath =
    "M 0 -12 L 3 -3 L 12 0 L 3 3 L 0 12 L -3 3 L -12 0 L -3 -3 Z";
  stars.appendChild(
    s("path", { d: starPath, transform: "translate(-25, -100)" }),
  );
  stars.appendChild(
    s("path", { d: starPath, transform: "translate(25, -100)" }),
  );
  g.appendChild(stars);
}

/** Crown central emblem: concentric glow + a slim star. */
function appendCrownCenter(g, gColor) {
  g.appendChild(
    s("circle", { cx: 0, cy: 15, r: 32, fill: gColor, opacity: 0.1 }),
  );
  g.appendChild(
    s("circle", { cx: 0, cy: 15, r: 15, fill: "#E2E8F0", opacity: 0.15 }),
  );
  g.appendChild(
    s("path", {
      d: "M 0 -12 L 2 -3 L 12 0 L 2 3 L 0 12 L -2 3 L -12 0 L -2 -3 Z",
      transform: "translate(0, 15)",
      fill: "#E2E8F0",
      opacity: 0.4,
    }),
  );
}

const FACE_MOTIF_SCALE_KEY = {
  jack: "motifScaleJack",
  queen: "motifScaleQueen",
  king: "motifScaleKing",
};

export function addFaceMotif(parent, rank, color) {
  const scale = CARD_GEOMETRY[FACE_MOTIF_SCALE_KEY[rank]];
  parent.appendChild(getAtmosphere(color)); // outside the scale wrapper — see getAtmosphere
  withMotifScale(parent, scale, (target) => {
    switch (rank) {
      case "jack":
        addJackMotif(target, color);
        break;
      case "queen":
        addQueenMotif(target, color);
        break;
      case "king":
        addKingMotif(target, color);
        break;
    }
  });
}
